import { assetSizeError, ASSET_MAX_BYTES } from '@shared/media/uploadPolicy';
import { MediaProcessingError } from './errors';
import type {
  MediaProcessingMetadata,
  MediaProcessingUse,
  MediaQualityIntent,
} from './types';

export const IMAGE_PROCESSING_POLICY_VERSION = 'media-client-v1';

export type ProcessedImage = {
  file: File;
  metadata: MediaProcessingMetadata;
  policyVersion: string;
  source: MediaProcessingMetadata;
};

type ImageProcessingOptions = {
  use: MediaProcessingUse;
  quality?: MediaQualityIntent;
  policyVersion?: string;
  signal?: AbortSignal;
};

const IMAGE_LIMITS: Record<MediaProcessingUse, { maxDimension: number; quality: number }> = {
  chat: { maxDimension: 2048, quality: 0.78 },
  article: { maxDimension: 2560, quality: 0.82 },
  avatar: { maxDimension: 1024, quality: 0.84 },
  profileBackground: { maxDimension: 2048, quality: 0.8 },
};

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) {
    throw new MediaProcessingError('cancelled', '图片处理已取消');
  }
}

function sourceMetadata(file: File): MediaProcessingMetadata {
  return { mimeType: file.type || 'application/octet-stream', fileName: file.name, sizeBytes: file.size };
}

function outputName(name: string, mimeType: string) {
  const stem = name.replace(/\.[^.]+$/, '') || 'image';
  return `${stem}-optimized.${mimeType === 'image/webp' ? 'webp' : 'jpg'}`;
}

async function decodeImage(file: File, signal?: AbortSignal): Promise<ImageBitmap | HTMLImageElement> {
  throwIfAborted(signal);
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Some browsers expose createImageBitmap but do not support the option.
    }
  }
  if (typeof Image === 'undefined') {
    throw new MediaProcessingError('unsupported-input', '当前浏览器不支持图片解码');
  }
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    await image.decode();
    throwIfAborted(signal);
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function hasTransparency(ctx: CanvasRenderingContext2D, width: number, height: number) {
  // Sample rather than retaining a second full-size pixel buffer on large images.
  const sampleWidth = Math.min(width, 256);
  const sampleHeight = Math.min(height, 256);
  const sample = document.createElement('canvas');
  sample.width = sampleWidth;
  sample.height = sampleHeight;
  const sampleContext = sample.getContext('2d', { willReadFrequently: true });
  if (!sampleContext) return false;
  sampleContext.drawImage(ctx.canvas, 0, 0, sampleWidth, sampleHeight);
  const pixels = sampleContext.getImageData(0, 0, sampleWidth, sampleHeight).data;
  for (let index = 3; index < pixels.length; index += 4) {
    if (pixels[index] < 255) return true;
  }
  return false;
}

function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string, quality: number, signal?: AbortSignal) {
  throwIfAborted(signal);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new MediaProcessingError('output-invalid', '图片编码失败'));
      } else {
        resolve(blob);
      }
    }, mimeType, quality);
  });
}

/** Decode, resize and encode an image before an upload session is created. */
export async function processImage(file: File, options: ImageProcessingOptions): Promise<ProcessedImage> {
  const policyVersion = options.policyVersion ?? IMAGE_PROCESSING_POLICY_VERSION;
  const source = sourceMetadata(file);
  if (!file.type.startsWith('image/')) {
    throw new MediaProcessingError('unsupported-input', '请选择图片文件');
  }
  if (file.size <= 0) {
    throw new MediaProcessingError('unsupported-input', '图片文件为空');
  }
  throwIfAborted(options.signal);

  const decoded = await decodeImage(file, options.signal);
  const width = decoded.width;
  const height = decoded.height;
  if (!width || !height || width * height > 40_000_000) {
    throw new MediaProcessingError('resource-exhausted', '图片尺寸超过客户端处理预算');
  }
  const limits = IMAGE_LIMITS[options.use];
  const scale = Math.min(1, limits.maxDimension / Math.max(width, height));
  const outputWidth = Math.max(1, Math.round(width * scale));
  const outputHeight = Math.max(1, Math.round(height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const context = canvas.getContext('2d', { alpha: true });
  if (!context) throw new MediaProcessingError('unsupported-input', '当前浏览器无法创建图片处理画布');
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(decoded, 0, 0, outputWidth, outputHeight);
  if ('close' in decoded && typeof decoded.close === 'function') decoded.close();
  throwIfAborted(options.signal);

  // Opaque PNG/GIF inputs are allowed to become JPEG; retain PNG only when
  // the decoded pixels actually contain alpha so transparency is not lost.
  const keepTransparency = hasTransparency(context, outputWidth, outputHeight);
  const mimeType = keepTransparency ? 'image/webp' : 'image/jpeg';
  const quality = options.quality === 'hd' ? Math.min(0.92, limits.quality + 0.1) : limits.quality;
  const blob = await canvasToBlob(canvas, mimeType, quality, options.signal);
  const outputSizeError = assetSizeError(blob.size);
  if (outputSizeError) {
    throw new MediaProcessingError('output-invalid', `压缩后的图片仍超过 10 MiB (${blob.size} bytes)`);
  }
  if (blob.size <= 0) throw new MediaProcessingError('output-invalid', '图片输出为空');
  const outputFile = new File([blob], outputName(file.name, mimeType), { type: mimeType, lastModified: Date.now() });
  if (outputFile.size > ASSET_MAX_BYTES) throw new MediaProcessingError('output-invalid', '图片输出超过大小限制');
  const metadata: MediaProcessingMetadata = {
    mimeType,
    fileName: outputFile.name,
    sizeBytes: outputFile.size,
    width: outputWidth,
    height: outputHeight,
  };
  return { file: outputFile, metadata, policyVersion, source: { ...source, width, height } };
}
